/**
 * 同意書セクション定義 (シンプル版)。
 *
 * Document の registry と違い、各 type に variant を持たない。
 * 必要に応じて Phase 3 以降で拡張可能。
 *
 * signature セクションは **必須** で、追加 UI からは選択できない (backend が末尾に強制配置)。
 */
import type { ConsentSection, ConsentSectionType } from '@goenhub/shared';
import {
  AlertCircle,
  CheckSquare,
  Heading1,
  ImageIcon,
  List,
  ListOrdered,
  Minus,
  PenLine,
  Type,
} from 'lucide-react';
import type { ComponentType } from 'react';

export interface ConsentSectionDef {
  type: ConsentSectionType;
  label: string;
  icon: ComponentType<{ className?: string }>;
  description: string;
  /** 追加メニューに表示するか (signature は false で常に末尾固定) */
  selectable: boolean;
  sample(): Record<string, unknown>;
}

export const CONSENT_SECTION_DEFS: Record<ConsentSectionType, ConsentSectionDef> = {
  heading: {
    type: 'heading',
    label: '見出し',
    icon: Heading1,
    description: '章タイトル・大きな見出し',
    selectable: true,
    sample: () => ({ text: '第1条 ...', level: 2 }),
  },
  paragraph: {
    type: 'paragraph',
    label: '段落',
    icon: Type,
    description: '本文の段落',
    selectable: true,
    sample: () => ({ text: '本同意書は……。' }),
  },
  bullet_list: {
    type: 'bullet_list',
    label: '箇条書き',
    icon: List,
    description: '大項目（タイトル+説明+子項目）の点リスト',
    selectable: true,
    sample: () => ({
      items: [
        { title: '大項目 1', description: '', children: [] },
        { title: '大項目 2', description: '', children: [] },
      ],
    }),
  },
  numbered_list: {
    type: 'numbered_list',
    label: '番号リスト',
    icon: ListOrdered,
    description: '大項目（タイトル+説明+子項目）の番号付きリスト',
    selectable: true,
    sample: () => ({
      items: [
        { title: '第 1 項', description: '', children: [] },
        { title: '第 2 項', description: '', children: [] },
      ],
    }),
  },
  callout: {
    type: 'callout',
    label: '注意書き',
    icon: AlertCircle,
    description: '強調表示の枠囲み',
    selectable: true,
    sample: () => ({ text: '※ 重要な注意事項', tone: 'warning' }),
  },
  divider: {
    type: 'divider',
    label: '区切り線',
    icon: Minus,
    description: '横線',
    selectable: true,
    sample: () => ({}),
  },
  image: {
    type: 'image',
    label: '画像',
    icon: ImageIcon,
    description: '本文中の画像',
    selectable: true,
    sample: () => ({ storagePath: null, alt: '' }),
  },
  signature: {
    type: 'signature',
    label: '署名フィールド (必須)',
    icon: PenLine,
    description: '末尾に自動配置。削除不可。',
    selectable: false,
    sample: () => ({
      label: '署名',
      signatureFieldLabel: '氏名',
      dateFieldLabel: '署名日',
    }),
  },
  consent_check: {
    type: 'consent_check',
    label: '同意チェック',
    icon: CheckSquare,
    description: '提出条件 (チェック ON が必須)',
    selectable: true,
    sample: () => ({
      statement: '上記の内容に同意して申し込みをします。',
    }),
  },
};

export const CONSENT_SECTION_ORDER: ConsentSectionType[] = [
  'heading',
  'paragraph',
  'bullet_list',
  'numbered_list',
  'callout',
  'divider',
  'image',
  'consent_check',
];

export function newSectionId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function makeSection(type: ConsentSectionType): ConsentSection {
  return {
    id: newSectionId(),
    type,
    config: CONSENT_SECTION_DEFS[type].sample(),
  };
}
